import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL,
  PREMIUM_AUTH_UNLOCK_CTA_LABEL,
  PREMIUM_GUEST_CONTINUE_CTA_LABEL,
  PREMIUM_ONE_TIME_NOTE,
  PREMIUM_PAYMENT_TRUST_LINE,
  PREMIUM_PRICE_PERIOD_LABEL,
  getUpsellHeadline,
  getUpsellHook,
} from '../../constants/pricing';
import { FEATURE_UPSELL_LABELS } from '../../constants/pricingFeatures';
import { usePremiumUpgrade } from '../../hooks/usePremiumUpgrade';
import { resolveUserTier, trackPremiumCtaClick, trackPricingView } from '../../utils/analytics';
import { trackViewPromotionAB, trackSelectPromotionAB } from '../../utils/analyticsAB';
import { withNextQuery, withUpgradeIntent } from '../../utils/safeNextPath';
import { useABTest } from '../../hooks/useABTest';
import PricingFeaturesComparison from '../PricingFeaturesComparison/PricingFeaturesComparison';
import CheckoutErrorNotice from '../CheckoutErrorNotice/CheckoutErrorNotice';
import PremiumUpsellModalB from './PremiumUpsellModalB';
import './PremiumUpsellModal.css';

/**
 * PremiumUpsellModal — A/B test router
 *
 * Variant A (control): original layout — generic title, plain price block, feature table, full CTA label.
 * Variant B (challenger): benefit headline, hook sentence, price anchoring, social proof, short CTA.
 *
 * Both variants fire view_promotion / select_promotion with ab_variant dimension attached.
 */
const PremiumUpsellModal = ({
  isOpen,
  onClose,
  feature = null,
  user = null,
  institutionSlug = '',
  placement = 'upsell_modal_a',
}) => {
  const navigate = useNavigate();
  const isAuthenticated = user !== null;
  const isPremium = user?.is_premium_active === true;
  const userTier = resolveUserTier(user);
  const abVariant = useABTest(); // 'A' | 'B' | null (null while hydrating)
  const returnPath = withUpgradeIntent(window.location.pathname, feature);
  const source = 'upsell_modal';

  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath,
    onBeforeRedirect: onClose,
    source,
    feature,
    institutionSlug,
    placement,
  });

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && abVariant === 'A') {
      trackViewPromotionAB(feature, userTier, 'A');
      trackPricingView({ feature, userTier, placement: 'upsell_modal_a' });
    }
  }, [isOpen, feature, userTier, abVariant]);

  // Delegate to Variant B component when assigned
  if (abVariant === 'B') {
    return (
      <PremiumUpsellModalB
        isOpen={isOpen}
        onClose={onClose}
        feature={feature}
        user={user}
        institutionSlug={institutionSlug}
        placement={placement === 'upsell_modal_a' ? 'upsell_modal_b' : placement}
      />
    );
  }

  // Variant A (control) — or null while variant not yet resolved (renders nothing)
  if (!isOpen || !abVariant) return null;

  const handleRegister = () => {
    trackSelectPromotionAB('register', feature, 'A');
    trackPremiumCtaClick({
      feature,
      cta: 'register',
      userTier,
      institutionSlug,
      placement: 'upsell_modal_a',
      source,
    });
    onClose();
    navigate(withNextQuery('/register', returnPath));
  };

  const handleLogin = () => {
    trackSelectPromotionAB('login', feature, 'A');
    onClose();
    navigate(withNextQuery('/login', returnPath));
  };

  const handleUpgradeClick = () => {
    trackSelectPromotionAB('upgrade', feature, 'A');
    startCheckout();
  };

  const getFeatureDescription = () => {
    if (feature && FEATURE_UPSELL_LABELS[feature]) {
      return FEATURE_UPSELL_LABELS[feature];
    }
    return 'Premium funktioner';
  };

  const featureNoteText = (() => {
    if (!feature) return null;
    if (feature === 'compare' && !isAuthenticated) {
      return (
        <>
          To institutioner kan sammenlignes gratis. Ubegrænset sammenligning og historik kræver{' '}
          <strong>Premium</strong>.
        </>
      );
    }
    return (
      <>
        Denne funktion kræver <strong>Premium</strong>: <strong>{getFeatureDescription()}</strong>
      </>
    );
  })();

  const modal = (
    <div className="premium-upsell-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="premium-upsell-modal" onClick={(e) => e.stopPropagation()} role="document">
        <button className="premium-upsell-modal-close" onClick={onClose} aria-label="Luk">
          ×
        </button>

        <div className="premium-upsell-modal-content">
          <h2 className="premium-upsell-modal-title">
            {getUpsellHeadline({ isAuthenticated, feature })}
          </h2>
          <p className="premium-upsell-modal-hook">{getUpsellHook(feature)}</p>

          <div className="premium-upsell-modal-price">
            <div>
              <span className="premium-price-amount">{PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL}</span>
              <span className="premium-price-period">{PREMIUM_PRICE_PERIOD_LABEL}</span>
            </div>
            <p className="premium-price-note">{PREMIUM_ONE_TIME_NOTE}</p>
            <p className="premium-price-note">{PREMIUM_PAYMENT_TRUST_LINE}</p>
          </div>

          <div className="premium-upsell-modal-features">
            <h3>Basis vs Premium</h3>
            <PricingFeaturesComparison variant="modal" />
          </div>

          {featureNoteText && (
            <div className="premium-upsell-modal-feature-note">
              <p>{featureNoteText}</p>
            </div>
          )}

          <div className="premium-upsell-modal-actions">
            <CheckoutErrorNotice
              message={checkoutError}
              onRetry={!isAuthenticated ? undefined : handleUpgradeClick}
            />
            {!isAuthenticated ? (
              <>
                <button
                  className="premium-upsell-button premium-upsell-button-primary"
                  onClick={handleRegister}
                >
                  {PREMIUM_GUEST_CONTINUE_CTA_LABEL}
                </button>
                <button
                  className="premium-upsell-button premium-upsell-button-secondary"
                  onClick={handleLogin}
                >
                  Log ind
                </button>
              </>
            ) : !isPremium ? (
              <button
                className="premium-upsell-button premium-upsell-button-primary"
                onClick={handleUpgradeClick}
                disabled={isStartingCheckout}
              >
                {isStartingCheckout ? 'Åbner betaling...' : PREMIUM_AUTH_UNLOCK_CTA_LABEL}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default PremiumUpsellModal;
